/** Amplify AppSync observeQuery / subscriptions reject with plain objects, not Error. */

export type AmplifySubscriptionError = {
  type?: string;
  error?: { errors?: Array<{ message?: string }> };
};

const SYNC_EVENT_TYPES = new Set(['onCreate', 'onUpdate', 'onDelete']);

export function isAmplifySubscriptionError(err: unknown): err is AmplifySubscriptionError {
  return (
    typeof err === 'object' &&
    err !== null &&
    typeof (err as AmplifySubscriptionError).type === 'string' &&
    SYNC_EVENT_TYPES.has((err as AmplifySubscriptionError).type as string)
  );
}

export function formatAmplifySubscriptionError(err: unknown): string {
  if (isAmplifySubscriptionError(err)) {
    const msg = err.error?.errors?.map((e) => e.message).filter(Boolean).join('; ');
    return `${err.type}: ${msg || 'sync authorization failed'}`;
  }
  if (err instanceof Error) return err.message;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

type SubscribeHandlers<T> = {
  next?: (data: T) => void;
  error?: (error: unknown) => void;
};

type Subscribable<T> = {
  subscribe: (handlers: SubscribeHandlers<T>) => { unsubscribe: () => void };
};

function logAmplifySyncFailure(label: string, err: unknown): void {
  if (isAmplifySubscriptionError(err)) {
    console.debug(`[ShopGND] ${label}`, formatAmplifySubscriptionError(err));
    return;
  }
  console.warn(`[ShopGND] ${label}`, formatAmplifySubscriptionError(err));
}

/** Subscribe with a default error handler so AppSync sync failures are not uncaught. */
export function subscribeAmplify<T>(
  subscription: Subscribable<T>,
  handlers: SubscribeHandlers<T>,
  label: string,
): { unsubscribe: () => void } {
  return subscription.subscribe({
    next: (data) => {
      try {
        handlers.next?.(data);
      } catch (err) {
        if (handlers.error) handlers.error(err);
        else logAmplifySyncFailure(label, err);
      }
    },
    error: (err) => {
      if (handlers.error) {
        handlers.error(err);
        return;
      }
      logAmplifySyncFailure(label, err);
    },
  });
}

/** Catch Amplify observeQuery onCreate/onUpdate/onDelete failures (sync + thrown objects). */
export function installAmplifySubscriptionRejectionHandler(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('unhandledrejection', (event) => {
    if (!isAmplifySubscriptionError(event.reason)) return;
    event.preventDefault();
    logAmplifySyncFailure('subscription sync', event.reason);
  });

  window.addEventListener('error', (event) => {
    if (!isAmplifySubscriptionError(event.error)) return;
    event.preventDefault();
    logAmplifySyncFailure('subscription sync', event.error);
  });
}
